import { useId } from "react";
import type { ProductionStaticBody, ProductionVideoBody } from "@/types/productionBrief.type";
import {
  isEndFrameTitle,
  lengthSentence,
  parsedTotal,
  shotNumber,
  shotTimings,
  staticFormatLine,
  staticSizeLine,
} from "./briefParts";
import { BriefSection, FieldLabel, SectionHead, type BriefAudience } from "./BriefPrimitives";
import { BriefTreatment, BriefTreatmentStatic } from "./BriefTreatment";
import { BriefTimeline, type TimelineSegment } from "./BriefTimeline";
import { BriefShotRow } from "./BriefShotRow";
import { BriefInputs } from "./BriefInputs";
import { BriefCaptions } from "./BriefCaptions";
import { SlideRow, SlideStrip } from "./SlideParts";
import { ArtDirection } from "./BriefStaticSections";
import { BriefRules, BriefScript, BriefShootDay } from "./BriefVideoSections";

function scrollToRow(id: string) {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  document.getElementById(id)?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
}

/**
 * The video brief, read view (Figma 08): Treatment, Shot list, Voiceover,
 * Production inputs, Shoot day, Rules, Post captions. No Deliverables (CEO
 * note). Every section shows only when the brief fills it. `askedSeconds` is the idea's length, when it has one.
 */
export function BriefVideoBody({
  body,
  askedSeconds,
  aspectRatio,
  audience = "team",
  brandName,
  onChooseVisual,
}: {
  body: ProductionVideoBody;
  /** The brand, for the Length line's "tell {brand}" when the cut runs over. */
  brandName?: string | null;
  askedSeconds?: number | null;
  /** The post's frame; the shot pictures follow it (9:16 when unknown). */
  aspectRatio?: string | null;
  audience?: BriefAudience;
  /** Present when the reader can save a pick: an empty shot opens the visual picker. */
  onChooseVisual?: (index: number) => void;
}) {
  const rowPrefix = useId();
  const shots = body.storyboard ?? [];
  const durations = shots.map((shot) => shot.duration);
  const timings = shotTimings(durations);
  const labels = shots.map((shot, index) =>
    index === shots.length - 1 && index > 0 && isEndFrameTitle(shot.title) ? "End" : shotNumber(index),
  );
  const segments: TimelineSegment[] = timings.flatMap((timing, index) =>
    timing.seconds !== null ? [{ index, label: labels[index]!, seconds: timing.seconds }] : [],
  );
  const rowId = (index: number) => `${rowPrefix}-shot-${index}`;

  return (
    <div className="@container/briefdoc flex w-full min-w-0 flex-col gap-11">
      <BriefTreatment
        hookVo={body.hookVo}
        conceptCues={body.conceptCues}
        soundDirection={body.soundDirection}
        length={lengthSentence(durations, askedSeconds, brandName)}
        ctaTextOnScreen={body.ctaTextOnScreen}
      />
      {shots.length ? (
        <BriefSection accent="indigo">
          <SectionHead accent="indigo" title="Shot list" sub={`${shots.length} ${shots.length === 1 ? "shot" : "shots"}`} className="mb-5" />
          {segments.length >= 2 ? (
            <BriefTimeline
              segments={segments}
              total={parsedTotal(timings)}
              askedSeconds={askedSeconds}
              complete={timings.every((timing) => timing.seconds !== null)}
              onSelect={(index) => scrollToRow(rowId(index))}
            />
          ) : null}
          <div className="mt-3">
            {shots.map((scene, index) => (
              <BriefShotRow
                key={scene.id ?? index}
                id={rowId(index)}
                scene={scene}
                label={labels[index]!}
                timing={timings[index]!}
                aspect={aspectRatio}
                onChooseVisual={onChooseVisual ? () => onChooseVisual(index) : undefined}
              />
            ))}
          </div>
        </BriefSection>
      ) : null}
      <BriefScript script={body.script} voiceovers={shots.map((shot) => shot.voiceover)} />
      <BriefInputs
        props={body.props}
        wardrobe={body.wardrobe}
        locations={body.locations}
        talent={body.talent}
        crew={body.crew}
      />
      <BriefShootDay schedule={body.schedule} />
      <BriefRules conceptLock={body.conceptLock} legal={body.legalMandatories} />
      <BriefCaptions distribution={body.distribution} audience={audience} />
    </div>
  );
}

/**
 * The static brief, read view (Figma 08, "the static brief as a carousel"):
 * The picture first (one picture, or a carousel numbered 1 to N), then
 * Treatment, Art direction, Production inputs, Shoot day, Rules and Post
 * captions with the alt text. No Deliverables. Every section shows only when
 * the brief fills it.
 */
export function BriefStaticBody({
  body,
  audience = "team",
  onChooseVisual,
}: {
  body: ProductionStaticBody;
  audience?: BriefAudience;
  /** Present when the reader can save a pick: an empty slide opens the visual picker. */
  onChooseVisual?: (index: number) => void;
}) {
  const rowPrefix = useId();
  const panels = body.panels ?? [];
  const altText = body.altText?.trim();
  const format = staticFormatLine(panels);
  const size = staticSizeLine(panels);
  return (
    <div className="@container/briefdoc flex w-full min-w-0 flex-col gap-11">
      {panels.length ? (
        <BriefSection accent="indigo">
          <SectionHead
            accent="indigo"
            title="The picture"
            sub={panels.length === 1 ? "One picture" : `A carousel of ${panels.length} pictures`}
          />
          {format || size ? (
            <p className="-mt-2.5 mb-[18px] text-[12.5px] leading-[17px] text-co-muted">{[format, size].filter(Boolean).join(" · ")}</p>
          ) : null}
          {panels.length > 1 ? <SlideStrip panels={panels} onChooseVisual={onChooseVisual} /> : null}
          <div className={panels.length > 1 ? "mt-6" : undefined}>
            {panels.map((panel, index) => (
              <SlideRow
                key={panel.id ?? index}
                id={`${rowPrefix}-slide-${index}`}
                panel={panel}
                index={index}
                onChooseVisual={onChooseVisual}
              />
            ))}
          </div>
        </BriefSection>
      ) : null}
      <BriefTreatmentStatic
        headline={body.headline}
        format={format}
        size={size}
        ctaTextOnArtwork={body.ctaTextOnArtwork}
      />
      <ArtDirection art={body.artDirection} />
      <BriefInputs
        props={body.props}
        wardrobe={body.wardrobe}
        locations={body.locations}
        talent={body.talent}
        crew={body.crew}
      />
      <BriefShootDay schedule={body.schedule} />
      <BriefRules conceptLock={body.conceptLock} legal={body.legalMandatories} />
      <BriefCaptions
        distribution={body.distribution}
        audience={audience}
        masterCaption={{ caption: body.caption }}
      >
        {altText ? (
          <div className="mt-6">
            <FieldLabel>Alt text, for screen readers</FieldLabel>
            <p className="mt-1.5 whitespace-pre-wrap text-[15px] leading-[1.5] text-co-text">{altText}</p>
          </div>
        ) : null}
      </BriefCaptions>
    </div>
  );
}
